import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { Barn, Horse, Owner } from "../../models";
import Spinner from "../Spinners/Spinner";
import { IonCol } from "@ionic/react";
import ErrorAlert from "../Errors/ErrorAlert";
import VerifyOwnerModal from "./VerifyOwnerModal";
import { formattedOption } from "../../utilities/owner/SelectOrCreateOwnerUtils";
import { formatShortAddress } from "../../utilities/address/FormatAddress";
const { v4: uuidv4 } = require("uuid");

interface SelectOrCreateOwnerProps {
    userOwnerOptions?: (formattedOption[] | null | undefined)
    barnOwnerOptions?: (formattedOption[] | null | undefined)
    horseOwnerOptions?: (formattedOption[] | null | undefined)

    selectedBarn?: (Barn | null | undefined)
    selectedHorse?: (Horse | null | undefined)
    selectedValue?: string | null;
    onSelect: (owner?: Owner, ownerName?: string) => void;
    isHeightRequired?: boolean;
    height?: string;
}

/**
 * This function is passed into the select component to define what should be shown when an option is selected
 * Instead of showing the 3 lines from the option,
 * it will only show the 1 line defined below: ownerName
 * @param props
 * @returns
 */
const SingleValue = (props: any) => <components.SingleValue {...props}>{props.data.ownerName || props.data.label}</components.SingleValue>;

const SelectOrCreateOwner: React.FC<SelectOrCreateOwnerProps> = ({userOwnerOptions, barnOwnerOptions, horseOwnerOptions, selectedBarn, selectedHorse, selectedValue, onSelect, isHeightRequired, height }) => {

    // Dropdown Section Titles
    const createdByUserGroupLabel = "Owners Created by You";
    const barnOwnersGroupLabel = selectedBarn ? (selectedBarn.name + " Owners") : "Barn Owners";
    const horseOwnersGroupLabel = selectedHorse ? (selectedHorse.name + " Owner") : "Horse Owners";

    // Groups - each group represents a section in the drop down
    const barnOwnersGroup = {
        label: barnOwnersGroupLabel,
        options:
            barnOwnerOptions && barnOwnerOptions.length > 0
                ? barnOwnerOptions
                : [{ value: selectedBarn ? `No owners are in ${selectedBarn?.name}` : `No barn selected.`, label: " ", ownerName: " " }],
    };

    const ownersCreatedByUserGroup = {
        label: createdByUserGroupLabel,
        options: userOwnerOptions && userOwnerOptions.length > 0 ? userOwnerOptions : [{ value: "You have not created any owners yet.", label: "", ownerName: "" }],
    };

    const horseOwnersGroup = {
        label: horseOwnersGroupLabel,
        options: ((horseOwnerOptions && horseOwnerOptions.length > 0) ? horseOwnerOptions : [{ value: "No owner found for selected horse.", label: "", ownerName: "" }]),
    };

    // General State
    const [currentValue, setCurrentValue] = useState<formattedOption | null | undefined>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const findValueInList = (value: string) => {
        // Set up the options for arrays of owners that should be checked
        const optionArray = (userOwnerOptions || [])?.concat((barnOwnerOptions || [])).concat((horseOwnerOptions || []));
        // If there are options, loop through them and set the current value
        if (optionArray) {
            for (var i = 0; i < optionArray.length; i++) {
                const currentOption = optionArray[i];
                if (currentOption.value === value) {
                    setCurrentValue(currentOption);
                }
            }
        }
    };

    if (selectedValue && !currentValue) {
        findValueInList(selectedValue);
    }

    useEffect(() => {
        if (selectedValue) {
            const optionArray = (userOwnerOptions || []).concat(barnOwnerOptions || []).concat(horseOwnerOptions || []);
            const match = optionArray.find((o) => o.value === selectedValue);
            if (match) {
                setCurrentValue(match);
            }
        } else {
            setCurrentValue(null);
        }
    }, [selectedValue, userOwnerOptions, barnOwnerOptions, horseOwnerOptions]);

    // DROPDOWN Functions
    const handleOnChange = async (event?: any) => {
        // Owner was selected from either the barnOwners or ownersCreatedByUser groups
        if (event && event.owner) {
            await setCurrentValue(event);
            onSelect(event.owner);
        }

        // Option from the dropdown was created by the current user with only the owner name
        else if (event && event.ownerName && event.ownerName !== " ") {
            const newFormattedOption = handleCreateNewFormattedOptionWithJustOwnerName(event.ownerNameLabel);
            await setCurrentValue(newFormattedOption);
            onSelect(undefined, event.ownerName);
        }

        // Option from the dropdown was that there are no owners in this section
        else if (event && event.ownerName && event.ownerName === " ") {
            // Open the modal and have the user type in a name and verify the owner.
            setIsModalVisible(true);
        }

        // Here - event.value will be the value the user typed into the CreateableSelect field
        else if (event && event.value) {
            // Set the currentValue in state so that it can be used inside of the modal.
            const newFormattedOption = handleCreateNewFormattedOptionWithJustOwnerName(event.value);
            await setCurrentValue(newFormattedOption);

            // Verify Owner - If the owner was not in the barnOwners or ownersCreatedByUser groups
            setIsModalVisible(true);
        }

        // User clicked the X button to clear the value.
        else {
            await setCurrentValue(null);
            onSelect(undefined);
        }
    };

    const handleCreateNewFormattedOptionWithJustOwnerName = (ownerName: string) => {
        const newFormattedOption: formattedOption = {
            value: uuidv4(),
            label: ownerName + " " + (selectedBarn?.name || ""),
            ownerName: ownerName,
            ownerNameLabel: ownerName,
            barnLabel: selectedBarn?.name || "",
            owner: undefined,
        };
        return newFormattedOption;
    };

    /**
     * User found the owner by name in the RSP Database
     * Send that owner info back to the parent component
     * And close the modal
     */
    const handleUseMatchedOwner = async (event: formattedOption) => {
        if (event.ownerName) {
            await setCurrentValue(event);
            onSelect(event.owner, event.ownerName);
            setIsModalVisible(false);
        } else {
            setError("No owner name was found.");
        }
    };
    /**
     * User did not find the owner by name in the RSP Database
     * Send that owner name back to the parent component in order to create a new owner
     * And close the modal
     */
    const handleCreateNewOwner = (newOwnerName: string) => {
        onSelect(undefined, newOwnerName);
        setIsModalVisible(false);
    };
    /**
     * User did not find the owner by name in the RSP Database.
     * Assume the user thought the owner existed and realized there is a typo in the owner name.
     * Send the user back to the starting point to try again.
     */
    const handleCancel = () => {
        onSelect(undefined);
        setIsModalVisible(false);
        setCurrentValue(null);
    };

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <IonCol size="10">
                    {error && <ErrorAlert error={error} width="12" />}
                    <CreatableSelect
                        id="select-or-create-owner"
                        inputId="select-or-create-owner-input"
                        placeholder="Type the owner's name"
                        styles={{
                            // Fixes the overlapping problem of the component
                            control: (baseStyles) => (isHeightRequired ? { ...baseStyles, height } : { ...baseStyles }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            group: (base, state) => ({
                                ...base,
                                // Group background color
                                backgroundColor: state.label === barnOwnersGroupLabel ? "var(--ion-color-info)" : state.label === createdByUserGroupLabel ? "var(--ion-color-secondary)" : "var(--ion-color-primary)",
                                padding: "10px",
                            }),
                            groupHeading: (base, state) => ({
                                ...base,
                                fontSize: "1em",
                                fontWeight: "bold",
                                // Section title color
                                color: "var(--ion-color-white)",
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                                ...base,
                                // Hover vs. no hover colors for owners in list
                                backgroundColor: isFocused || isSelected ? "var(--ion-color-light)" : "var(--ion-color-white)",
                                cursor: "pointer",
                                color: "var(--ion-color-primary)",
                            }),
                        }}
                        defaultValue={currentValue}
                        value={currentValue}
                        menuPortalTarget={document.body}
                        menuPlacement={"auto"}
                        isClearable
                        options={((horseOwnerOptions && (horseOwnerOptions.length > 0)) ? 
                            [barnOwnersGroup, ownersCreatedByUserGroup, horseOwnersGroup]
                            : 
                            [barnOwnersGroup, ownersCreatedByUserGroup]
                        )}
                        noOptionsMessage={({ inputValue }) => (!inputValue ? "No input found" : "No results found")}
                        formatOptionLabel={(option: any) =>
                            option.ownerNameLabel ? (
                                <div className="px-10">
                                    <div className="font-weight-bold text-default">{option.ownerNameLabel}</div>
                                    {barnOwnersGroup.options.includes(option) ? (
                                        // Display for barnOwnersGroup
                                        <div className="display-5 text-medium">
                                            <div>Created By: {option.createdByLabel || "Unknown"}</div>
                                            <div>Location: {option.locationLabel || "Unknown"}</div>
                                        </div>
                                    ) : ownersCreatedByUserGroup.options.includes(option) ? (
                                        // Display for ownersCreatedByUserGroup
                                        <div className="display-5 text-medium">
                                            <div>Barn: {option.barnLabel || "Unknown"}</div>
                                            <div>Location: {option.locationLabel || "Unknown"}</div>
                                        </div>
                                    ) : (horseOwnersGroup.options && horseOwnersGroup.options?.includes(option)) ? (
                                        // Display for horseOwnersGroup
                                        <div className="display-5 text-medium">
                                            <div>Barn: {option.barnLabel || "Unknown"}</div>
                                            <div>Created By: {option.createdByLabel || "Unknown"}</div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="p-0">
                                    <div className="font-weight-bold text-default">{option.textToDisplay || "Select or Create Owner: " + option.value}</div>
                                </div>
                            )
                        }
                        components={{ SingleValue }}
                        onChange={(event: any) => handleOnChange(event)}
                        createOptionPosition={"first"}
                    />
                </IonCol>
            )}
            {isModalVisible && (
                <VerifyOwnerModal
                    currentValue={currentValue}
                    handleCancel={() => handleCancel()}
                    handleUseMatchedOwner={(e: formattedOption) => handleUseMatchedOwner(e)}
                    handleCreateNewOwner={(newOwnerName: string) => handleCreateNewOwner(newOwnerName)}
                />
            )}
        </>
    );
};

export default SelectOrCreateOwner;
